// Ways to open the bug-report modal without finding its button: a keyboard shortcut, and a
// three-finger tap on a touch screen. (Browsers expose no finger count for trackpad clicks, so
// a trackpad's three-finger tap cannot be told apart from other input.)

/** Human label for the shortcut, as shown in tooltips and the modal. */
export function bugReportShortcutLabel(platform = globalThis.navigator?.platform || "") {
  return /Mac|iPhone|iPad/.test(platform) ? "⌃⌥B" : "Ctrl+Alt+B";
}

/**
 * Whether a keydown is the bug-report shortcut: Ctrl+Alt+B (Control+Option+B on a Mac).
 * Matched on the physical key, so it works on any layout: a non-Latin one reports another
 * letter ("и"), and Option turns it into "∫" on a Mac. Left alone is AltGr text input
 * (AltGr = Ctrl+Alt on Windows): AltGraph held, or a symbol typed (e.g. "{" on Hungarian).
 */
export function isBugReportShortcut(e) {
  if (!e || e.repeat || e.code !== "KeyB") return false;
  if (!e.ctrlKey || !e.altKey || e.metaKey || e.shiftKey) return false;
  if (typeof e.getModifierState === "function" && e.getModifierState("AltGraph")) return false;
  const typed = typeof e.key === "string" && [...e.key].length === 1 ? e.key : null;
  return typed === null || typed === "∫" || /\p{L}/u.test(typed);
}

/**
 * Detects a tap by exactly `fingers` touches: they all land, none travels more than
 * `maxMovePx`, and the last lifts within `maxDurationMs` of the first landing. Feed it touch
 * points by identifier; `up` returns true on the lift that completes such a tap.
 */
export function createMultiFingerTap({ fingers = 3, maxDurationMs = 600, maxMovePx = 24 } = {}) {
  const active = new Map();
  let startT = 0;
  let peak = 0;
  let spoiled = false;
  return {
    down(id, x, y, t) {
      if (active.size === 0) {
        startT = t;
        peak = 0;
        spoiled = false;
      }
      active.set(id, { x, y });
      peak = Math.max(peak, active.size);
    },
    move(id, x, y) {
      const p = active.get(id);
      if (p && Math.hypot(x - p.x, y - p.y) > maxMovePx) spoiled = true;
    },
    up(id, t) {
      if (!active.delete(id) || active.size > 0) return false;
      return !spoiled && peak === fingers && t - startT <= maxDurationMs;
    },
    cancel(id) {
      active.delete(id);
      spoiled = true;
    },
  };
}
